// Astrid Bow Effect Implementation
// Features: Magnificent white and gold elven bow materializes, draws a holy light arrow,
// fires with a golden/white ribbon trail, and explodes into glowing golden shards.

const CONFIG = {
    scale: 0.8,
    materializeDuration: 750,
    drawDuration: 1200,
    fireDuration: 375,
    impactDuration: 1200,
    trailLength: 20,
};

const spriteCache = new Map<string, HTMLCanvasElement>();

function getAstridBowChassisSprite(): HTMLCanvasElement {
    const key = 'astridBowChassis';
    let sprite = spriteCache.get(key);
    if (!sprite) {
        sprite = document.createElement('canvas');
        sprite.width = 300;
        sprite.height = 400;
        const ctx = sprite.getContext('2d');
        if (ctx) {
            const cx = 150;
            const cy = 200;

            // Symmetrical up/down. We'll define a draw function for one half and mirror it.

            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';

            const drawElvenLimb = (isUpper: boolean) => {
                ctx.save();
                if (!isUpper) {
                    ctx.translate(cx, cy);
                    ctx.scale(1, -1);
                    ctx.translate(-cx, -cy);
                }

                // --- PEARL WHITE INNER CORE (Holy Magic) ---
                ctx.beginPath();
                ctx.moveTo(cx - 5, cy - 30);
                ctx.bezierCurveTo(cx + 40, cy - 70, cx + 50, cy - 120, cx - 15, cy - 170);
                ctx.bezierCurveTo(cx + 20, cy - 130, cx + 20, cy - 80, cx - 15, cy - 30);
                ctx.closePath();
                const pearlGrad = ctx.createLinearGradient(cx - 20, cy - 170, cx + 50, cy - 30);
                pearlGrad.addColorStop(0, '#ffffff');
                pearlGrad.addColorStop(0.5, '#f4f4f4');
                pearlGrad.addColorStop(1, '#e0e0e0');
                ctx.fillStyle = pearlGrad;
                ctx.shadowBlur = 15;
                ctx.shadowColor = '#ffffff';
                ctx.fill();
                ctx.shadowBlur = 0; // reset

                // Inner bright gold highlight instead of teal
                ctx.beginPath();
                ctx.moveTo(cx + 10, cy - 50);
                ctx.bezierCurveTo(cx + 35, cy - 80, cx + 35, cy - 120, cx - 10, cy - 160);
                ctx.strokeStyle = '#fffacd';
                ctx.lineWidth = 3;
                ctx.stroke();

                // --- GOLDEN WING / FEATHER ACCENTS (Replacing Green Leaves) ---
                const drawHolyFeather = (lx: number, ly: number, scale: number, rot: number) => {
                    ctx.save();
                    ctx.translate(lx, ly);
                    ctx.rotate(rot);
                    ctx.scale(scale, scale);
                    ctx.beginPath();
                    ctx.moveTo(0, 0);
                    ctx.bezierCurveTo(-20, -10, -30, -30, 0, -50);
                    ctx.bezierCurveTo(10, -30, 15, -10, 0, 0);
                    const featherGrad = ctx.createLinearGradient(0, -50, 0, 0);
                    featherGrad.addColorStop(0, '#ffffff'); // bright tip
                    featherGrad.addColorStop(1, '#d4af37'); // gold base
                    ctx.fillStyle = featherGrad;
                    ctx.fill();
                    // Spine
                    ctx.beginPath();
                    ctx.moveTo(0, 0);
                    ctx.bezierCurveTo(-5, -20, -5, -40, 0, -50);
                    ctx.strokeStyle = '#fff5b0';
                    ctx.lineWidth = 1;
                    ctx.stroke();
                    ctx.restore();
                };
                
                // Feather cluster near grip
                drawHolyFeather(cx - 15, cy - 40, 0.8, -Math.PI / 4);
                drawHolyFeather(cx - 5, cy - 45, 1.2, -Math.PI / 6);
                drawHolyFeather(cx + 10, cy - 35, 0.9, 0);

                // Feather cluster near tip
                drawHolyFeather(cx - 10, cy - 140, 0.7, -Math.PI / 3);
                drawHolyFeather(cx, cy - 130, 0.6, -Math.PI / 6);


                // --- ORNATE GOLD CHASSIS ---
                // Outer gold spine
                ctx.beginPath();
                ctx.moveTo(cx - 20, cy - 20); // base at grip
                ctx.bezierCurveTo(cx - 30, cy - 40, cx - 10, cy - 80, cx + 25, cy - 100); // inner hook
                ctx.bezierCurveTo(cx + 50, cy - 120, cx + 20, cy - 160, cx - 20, cy - 180); // upper tip
                ctx.bezierCurveTo(cx - 40, cy - 150, cx - 5, cy - 110, cx - 5, cy - 90);
                ctx.bezierCurveTo(cx - 5, cy - 60, cx - 40, cy - 30, cx - 20, cy - 20);
                ctx.closePath();

                // Inner gold wrap
                ctx.moveTo(cx + 10, cy - 20);
                ctx.bezierCurveTo(cx + 40, cy - 40, cx + 60, cy - 80, cx + 45, cy - 130);
                ctx.bezierCurveTo(cx + 50, cy - 110, cx + 45, cy - 60, cx + 10, cy - 20);

                const goldGrad = ctx.createLinearGradient(cx - 40, cy - 180, cx + 60, cy - 20);
                goldGrad.addColorStop(0, '#fff5b0'); // highlight
                goldGrad.addColorStop(0.3, '#d4af37'); // metallic gold
                goldGrad.addColorStop(0.7, '#aa7700'); // dark gold
                goldGrad.addColorStop(1, '#ffdf00'); // bright base
                ctx.fillStyle = goldGrad;
                ctx.shadowBlur = 10;
                ctx.shadowColor = 'rgba(218, 165, 32, 0.5)'; // Golden shadow
                ctx.fill();
                ctx.shadowBlur = 0;

                // Gold edge highlights
                ctx.beginPath();
                ctx.moveTo(cx - 20, cy - 180);
                ctx.bezierCurveTo(cx + 20, cy - 160, cx + 50, cy - 120, cx + 25, cy - 100);
                ctx.strokeStyle = '#ffffee';
                ctx.lineWidth = 1.5;
                ctx.stroke();

                ctx.restore();
            };

            // Draw both halves
            drawElvenLimb(true);
            drawElvenLimb(false);

            // --- WHITE/CREAM LEATHER GRIP ---
            ctx.save();
            ctx.shadowBlur = 5;
            ctx.shadowColor = 'black';

            // Wrap shape
            const gw = 18, gh = 50;
            const gripGrad = ctx.createLinearGradient(cx - gw, 0, cx + gw, 0);
            gripGrad.addColorStop(0, '#dcdcdc'); // cream
            gripGrad.addColorStop(0.5, '#ffffff'); // pure white
            gripGrad.addColorStop(1, '#dcdcdc');

            ctx.beginPath();
            ctx.moveTo(cx - gw, cy - gh / 2);
            ctx.quadraticCurveTo(cx - gw + 5, cy, cx - gw, cy + gh / 2);
            ctx.lineTo(cx + gw, cy + gh / 2);
            ctx.quadraticCurveTo(cx + gw - 5, cy, cx + gw, cy - gh / 2);
            ctx.closePath();
            ctx.fillStyle = gripGrad;
            ctx.fill();

            // Leather wrapping lines (golden thread)
            ctx.strokeStyle = '#d4af37';
            ctx.lineWidth = 2;
            for (let i = -20; i <= 20; i += 8) {
                ctx.beginPath();
                ctx.moveTo(cx - gw + (i % 6 > 3 ? 2 : 0), cy + i); // slight zigzag
                ctx.lineTo(cx + gw - (i % 6 > 3 ? 0 : 2), cy + i + 5);
                ctx.stroke();
            }

            // Grip gold end-caps
            ctx.fillStyle = '#ffdf00';
            ctx.fillRect(cx - gw - 2, cy - gh / 2 - 4, gw * 2 + 4, 8);
            ctx.fillRect(cx - gw - 2, cy + gh / 2 - 4, gw * 2 + 4, 8);

            // Gem in the center of the grip (Golden/Topaz)
            ctx.beginPath();
            ctx.arc(cx, cy, 6, 0, Math.PI * 2);
            ctx.fillStyle = '#ffeb3b';
            ctx.shadowBlur = 10;
            ctx.shadowColor = '#ffc107';
            ctx.fill();
            ctx.beginPath();
            ctx.arc(cx - 2, cy - 2, 2, 0, Math.PI * 2);
            ctx.fillStyle = '#ffffff';
            ctx.fill();

            ctx.restore();
        }
        spriteCache.set(key, sprite);
    }
    return sprite;
}

// Particle Classes
class LightMote {
    x: number; y: number; vx: number; vy: number; life: number; maxLife: number; color: string; size: number;
    constructor(x: number, y: number, color: string) {
        this.x = x; this.y = y;
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 2 + 0.5;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.maxLife = 30 + Math.random() * 30;
        this.life = this.maxLife;
        this.color = color;
        this.size = 1 + Math.random() * 2.5;
    }
    update() {
        this.x += this.vx; this.y += this.vy;
        this.life--;
    }
    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        ctx.globalAlpha = this.life / this.maxLife;
        ctx.fillStyle = this.color;
        
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
        
        // Fast glow alternative: a lower alpha larger circle
        ctx.save();
        // Core mote
        ctx.globalAlpha = (this.life / this.maxLife) * 0.4;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
        
        // Fast glow (simulated with a larger, fainter arc)
        ctx.globalAlpha *= 0.5;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 2.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }
}

class GoldenShard {
    x: number; y: number; vx: number; vy: number; life: number; maxLife: number; color: string; rot: number; rotSpeed: number; size: number;
    constructor(x: number, y: number) {
        this.x = x; this.y = y;
        const angle = Math.random() * Math.PI * 2;
        const speed = 4 + Math.random() * 12;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.maxLife = 40 + Math.random() * 30;
        this.life = this.maxLife;
        
        // Randomly pick between bright gold, light yellow, or pure white
        const colors = ['#ffd700', '#ffeb3b', '#ffffff', '#fff5b0'];
        this.color = colors[Math.floor(Math.random() * colors.length)];
        
        this.rot = Math.random() * Math.PI * 2;
        this.rotSpeed = (Math.random() - 0.5) * 0.5;
        this.size = 5 + Math.random() * 10; // Slightly larger sizes to compensate for fewer shards
    }
    update() {
        this.x += this.vx; this.y += this.vy;
        this.vx *= 0.94; // friction
        this.vy *= 0.94;
        this.rot += this.rotSpeed;
        this.life--;
    }
    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        const a = this.life / this.maxLife;
        ctx.globalAlpha = a;
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rot);
        ctx.fillStyle = this.color;
        ctx.globalAlpha = a;

        const drawShardPath = () => {
            ctx.beginPath();
            ctx.moveTo(0, -this.size);
            ctx.lineTo(this.size * 0.3, 0);
            ctx.lineTo(0, this.size);
            ctx.lineTo(-this.size * 0.3, 0);
            ctx.closePath();
        };

        // Main shard body
        drawShardPath();
        ctx.fill();

        // Optimized glow (re-draw scaled)
        ctx.globalAlpha = a * 0.4;
        ctx.scale(1.6, 1.6);
        drawShardPath();
        ctx.fill();
        
        ctx.restore();
    }
}

class LightRibbonNode {
    x: number; y: number; age: number; maxAge: number;
    constructor(x: number, y: number) {
        this.x = x; this.y = y; this.age = 0; this.maxAge = 20;
    }
}

export type AstridBowEffect = {
    kind: 'astridBow'
    t: number
    sx: number; sy: number
    tx: number; ty: number
    data?: {
        phase: 'MATERIALIZE' | 'DRAW' | 'FIRE' | 'IMPACT'
        motes: LightMote[]
        shards: GoldenShard[]
        trailNodeFrames: LightRibbonNode[][] // Store history of positions for thick ribbon
        arrowProgress: number // 0 to 1 along the path
        bowRot: number
        distance: number
        angle: number
    }
}

export const createAstridBowEffect = (sx: number, sy: number, tx: number, ty: number): AstridBowEffect => ({
    kind: 'astridBow', t: 0, sx, sy, tx, ty
})

export const renderAstridBowEffect = (
    eff: AstridBowEffect,
    ctx: CanvasRenderingContext2D
): boolean => {
    if (!eff.data) {
        const dx = eff.tx - eff.sx;
        const dy = eff.ty - eff.sy;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx);

        eff.data = {
            phase: 'MATERIALIZE',
            motes: [],
            shards: [],
            trailNodeFrames: [],
            arrowProgress: 0,
            bowRot: angle, // Base aim angle
            distance,
            angle
        };
    }

    const d = eff.data;

    // Determine overall phase and local time
    if (eff.t < CONFIG.materializeDuration) d.phase = 'MATERIALIZE';
    else if (eff.t < CONFIG.materializeDuration + CONFIG.drawDuration) d.phase = 'DRAW';
    else if (eff.t < CONFIG.materializeDuration + CONFIG.drawDuration + CONFIG.fireDuration) d.phase = 'FIRE';
    else if (eff.t < CONFIG.materializeDuration + CONFIG.drawDuration + CONFIG.fireDuration + CONFIG.impactDuration) d.phase = 'IMPACT';
    else return false;

    // Phase local timers
    const tMat = Math.max(0, Math.min(1, eff.t / CONFIG.materializeDuration));
    const tDraw = Math.max(0, Math.min(1, (eff.t - CONFIG.materializeDuration) / CONFIG.drawDuration));
    const tFire = Math.max(0, Math.min(1, (eff.t - CONFIG.materializeDuration - CONFIG.drawDuration) / CONFIG.fireDuration));
    const tImpact = Math.max(0, Math.min(1, (eff.t - CONFIG.materializeDuration - CONFIG.drawDuration - CONFIG.fireDuration) / CONFIG.impactDuration));

    // Update Particles
    for (let i = d.motes.length - 1; i >= 0; i--) {
        d.motes[i].update();
        if (d.motes[i].life <= 0) {
            d.motes[i] = d.motes[d.motes.length - 1];
            d.motes.pop();
        }
    }
    for (let i = d.shards.length - 1; i >= 0; i--) {
        d.shards[i].update();
        if (d.shards[i].life <= 0) {
            d.shards[i] = d.shards[d.shards.length - 1];
            d.shards.pop();
        }
    }

    // Manage Trail Nodes
    if (d.trailNodeFrames.length > 0) {
        d.trailNodeFrames.forEach(frame => {
            frame.forEach(n => n.age++);
        });
        d.trailNodeFrames = d.trailNodeFrames.filter(frame => frame[0] && frame[0].age < frame[0].maxAge);
    }

    ctx.save();
    ctx.globalCompositeOperation = 'screen';

    // RENDER BOW
    if (d.phase === 'MATERIALIZE' || d.phase === 'DRAW' || d.phase === 'FIRE') {
        ctx.save();
        ctx.translate(eff.sx, eff.sy);

        let bowScale = CONFIG.scale;
        let bowAlpha = 1;
        let bowRecoil = 0;
        const maxBowPullback = 120; // Dramatic pullback of the entire bow

        // Draw distance (0 to 1)
        let pullAmount = 0;
        if (d.phase === 'DRAW') {
            pullAmount = 1 - Math.pow(1 - tDraw, 3);
            if (Math.random() < 0.4) {
                // Spawn holy light motes (white and gold)
                const isGold = Math.random() > 0.5;
                const moteColor = isGold ? '#ffd700' : '#ffffff';
                d.motes.push(new LightMote((Math.random() - 0.5) * 100, (Math.random() - 0.5) * 100, moteColor));
            }
        }

        if (d.phase === 'MATERIALIZE') {
            const easeOutBounce = (x: number): number => {
                const n1 = 7.5625, d1 = 2.75;
                if (x < 1 / d1) return n1 * x * x;
                else if (x < 2 / d1) return n1 * (x -= 1.5 / d1) * x + 0.75;
                else if (x < 2.5 / d1) return n1 * (x -= 2.25 / d1) * x + 0.9375;
                else return n1 * (x -= 2.625 / d1) * x + 0.984375;
            };
            bowScale = CONFIG.scale * easeOutBounce(tMat);
            bowAlpha = tMat;
            ctx.rotate(d.angle + Math.PI * (1 - tMat)); // Spin into place
        } else {
            ctx.rotate(d.angle);
            // Shake during max draw
            if (d.phase === 'DRAW') {
                if (tDraw > 0.8) {
                    ctx.translate((Math.random() - 0.5) * 4, (Math.random() - 0.5) * 4);
                }
                bowRecoil = -pullAmount * maxBowPullback;
            }
            if (d.phase === 'FIRE') {
                // Powerful kickback recoil: kicks further back away from the target
                const kickback = Math.pow(tFire, 0.5) * 60; // Sharp backward kick
                bowRecoil = -maxBowPullback - kickback;
                bowAlpha = 1 - tFire; // Fade out quickly after firing
            }
        }

        ctx.translate(bowRecoil, 0); // Apply recoil backwards
        ctx.scale(bowScale, bowScale);
        ctx.globalAlpha = bowAlpha;

        const sprite = getAstridBowChassisSprite();
        // Sprite center is cx=150, cy=200
        ctx.drawImage(sprite, -150, -200);

        const stringMidX = -150 - (pullAmount * 80); // String pulls BACK relative to the bow
        const stringMidY = -200; // Center grip Y is 0 in translation

        // String lines (Glowing pure white/gold)
        ctx.beginPath();
        ctx.moveTo(-20, -180); // Top tip
        ctx.lineTo(stringMidX + 150, 0);       // Nocking point
        ctx.lineTo(-20, 180);  // Bottom tip

        ctx.strokeStyle = `rgba(255, 235, 150, ${0.7 + pullAmount * 0.3})`;
        ctx.lineWidth = 3 + pullAmount * 2;
        ctx.shadowBlur = 15;
        ctx.shadowColor = '#fffacd';
        ctx.stroke();

        // The Arrow (While Draw)
        if (d.phase === 'DRAW') {
            const arrowLen = 150;
            const arrowTipX = stringMidX + 150 + arrowLen;

            ctx.beginPath();
            ctx.moveTo(stringMidX + 150, 0); // Nock
            ctx.lineTo(arrowTipX, 0); // Tip

            const grad = ctx.createLinearGradient(stringMidX + 150, 0, arrowTipX, 0);
            grad.addColorStop(0, 'rgba(255, 255, 255, 0)');
            grad.addColorStop(0.5, 'rgba(255, 215, 0, 1)'); // gold
            grad.addColorStop(1, 'rgba(255, 255, 255, 1)'); // white hot tip
            ctx.strokeStyle = grad;
            ctx.lineWidth = 6;
            ctx.lineCap = 'round';
            ctx.shadowBlur = 20;
            ctx.shadowColor = '#ffffff';
            ctx.stroke();

            // Holy Arrow head (Cross/diamond shape)
            ctx.beginPath();
            ctx.moveTo(arrowTipX + 25, 0);       // sharp point
            ctx.lineTo(arrowTipX, -12);
            ctx.lineTo(arrowTipX + 5, 0);
            ctx.lineTo(arrowTipX, 12);
            ctx.closePath();
            ctx.fillStyle = '#ffffff';
            ctx.fill();
            
            // White feather string nock at the back
            ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
            ctx.beginPath();
            ctx.moveTo(stringMidX + 150, 0);
            ctx.lineTo(stringMidX + 170, -10);
            ctx.lineTo(stringMidX + 160, 0);
            ctx.lineTo(stringMidX + 170, 10);
            ctx.closePath();
            ctx.fill();
        }

        // Draw local motes charging (handled globally below)

        ctx.restore();
    }

    // RENDER FIRE (ARROW TRAIL)
    if (d.phase === 'FIRE') {
        const maxBowPullback = 120;
        const arrowProgress = tFire; // 0 to 1

        // Parabolic arc for the arrow
        const arcHeight = -10; // Flatter arc than chromabow, feels faster/more direct
        const currentArc = Math.sin(arrowProgress * Math.PI) * arcHeight;

        // Calculate the base linear point
        // Arrow launches from the pulled back bow position
        const startX = eff.sx - Math.cos(d.angle) * maxBowPullback;
        const startY = eff.sy - Math.sin(d.angle) * maxBowPullback;
        const totalDist = d.distance + maxBowPullback;

        const baseX = startX + Math.cos(d.angle) * (totalDist * arrowProgress);
        const baseY = startY + Math.sin(d.angle) * (totalDist * arrowProgress);

        // Translate the point perpendicular to the angle to create the arc
        const currentX = baseX + Math.cos(d.angle - Math.PI / 2) * currentArc;
        const currentY = baseY + Math.sin(d.angle - Math.PI / 2) * currentArc;

        // Calculate tangent angle for the arrow rotation
        const nextProgress = Math.min(1, arrowProgress + 0.01);
        const nextArc = Math.sin(nextProgress * Math.PI) * arcHeight;
        const nextBaseX = startX + Math.cos(d.angle) * (totalDist * nextProgress);
        const nextBaseY = startY + Math.sin(d.angle) * (totalDist * nextProgress);
        const nextX = nextBaseX + Math.cos(d.angle - Math.PI / 2) * nextArc;
        const nextY = nextBaseY + Math.sin(d.angle - Math.PI / 2) * nextArc;

        const travelAngle = Math.atan2(nextY - currentY, nextX - currentX);

        // Record trail node frame (record two points for a thick ribbon)
        const perpAngle = travelAngle + Math.PI / 2;
        const width = 12; // Base width at head
        const frame = [
            new LightRibbonNode(currentX + Math.cos(perpAngle) * width, currentY + Math.sin(perpAngle) * width),
            new LightRibbonNode(currentX - Math.cos(perpAngle) * width, currentY - Math.sin(perpAngle) * width)
        ];
        d.trailNodeFrames.push(frame);

        // Spawn trailing sparks! (White and Gold)
        if (Math.random() < 0.8) {
            d.motes.push(new LightMote(currentX + (Math.random() - 0.5) * 20, currentY + (Math.random() - 0.5) * 20, Math.random() > 0.5 ? '#ffffff' : '#ffd700'));
        }

        // Draw Holy Light Projectile Arrow Tip
        ctx.save();
        ctx.translate(currentX, currentY);
        ctx.rotate(travelAngle);

        // --- White/Gold Core Halo ---
        ctx.beginPath();
        ctx.save();
        ctx.scale(0.5, 1); // oval shape
        ctx.arc(-10, 0, 18, 0, Math.PI * 2);
        ctx.restore();
        ctx.lineWidth = 4;

        // Halo gradient
        const ringGrad = ctx.createLinearGradient(-10, -18, -10, 18);
        ringGrad.addColorStop(0, 'rgba(255, 255, 255, 0)');
        ringGrad.addColorStop(0.3, '#ffd700'); // gold
        ringGrad.addColorStop(0.5, '#ffffff'); // blinding white center
        ringGrad.addColorStop(0.7, '#ffd700');
        ringGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.strokeStyle = ringGrad;
        ctx.stroke();

        ctx.shadowBlur = 20;
        ctx.shadowColor = '#ffffff';
        ctx.stroke();
        ctx.shadowBlur = 0;

        // --- Pure White Light Projectile Head ---
        const tipGrad = ctx.createLinearGradient(-30, 0, 50, 0);
        tipGrad.addColorStop(0, '#ffd700'); // Gold tail
        tipGrad.addColorStop(1, '#ffffff'); // White hot tip
        ctx.fillStyle = tipGrad;

        // Main spearhead
        ctx.beginPath();
        ctx.moveTo(60, 0);       // sharp intense tip
        ctx.lineTo(20, -15);     // top flare
        ctx.lineTo(10, -5);      // neck
        ctx.lineTo(-20, -15);    // back wing
        ctx.lineTo(-50, 0);      // tail stem
        ctx.lineTo(-20, 15);     // back wing
        ctx.lineTo(10, 5);       // neck
        ctx.lineTo(20, 15);      // bottom flare
        ctx.closePath();
        ctx.shadowBlur = 15;
        ctx.shadowColor = '#ffffff';
        ctx.fill();
        ctx.shadowBlur = 0;

        // Inner searing light core
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(60, 0);
        ctx.lineTo(20, -5);
        ctx.lineTo(-40, 0);
        ctx.lineTo(20, 5);
        ctx.closePath();
        ctx.fill();

        ctx.restore();

        if (tFire >= 0.99 && d.shards.length === 0) {
            // Initiate holy impact explosion shards
            for (let i = 0; i < 20; i++) d.shards.push(new GoldenShard(eff.tx, eff.ty));
        }
    }

    // RENDER RIBBON TRAIL
    if (d.trailNodeFrames.length > 2) {
        ctx.save();
        if (d.phase === 'IMPACT') ctx.globalAlpha = 1 - tImpact;
        else ctx.globalAlpha = 1;

        // Golden and White ribbons interleaving
        const colors = ['#ffffff', '#fffacd', '#ffd700', '#ffeb3b', '#ffffff'];
        const bands = colors.length;

        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        for (let b = 0; b < bands; b++) {
            // Offset multiplier maps from -1 (bottom) to 1 (top)
            const mapOffset = (b / (bands - 1)) * 2 - 1;

            const path = new Path2D();
            for (let i = 0; i < d.trailNodeFrames.length; i++) {
                const frame = d.trailNodeFrames[i];
                // Progress from tail (0) to head (1)
                const pointProgress = i / (d.trailNodeFrames.length - 1);

                // Fan out effect: wider at tail, narrower at head
                const fanWidth = 1 + (1 - pointProgress) * 1.5;

                // Base width interpolation
                const midX = (frame[0].x + frame[1].x) / 2;
                const midY = (frame[0].y + frame[1].y) / 2;

                // Vector representing 1 side from center
                const dx = frame[0].x - midX;
                const dy = frame[0].y - midY;

                // Apply the offset multiplier and the fanning effect
                const px = midX + dx * mapOffset * fanWidth;
                const py = midY + dy * mapOffset * fanWidth;

                if (i === 0) path.moveTo(px, py);
                else path.lineTo(px, py);
            }

            // Layer 3: Widest, faintest glow
            ctx.strokeStyle = colors[b];
            ctx.globalAlpha = 0.2;
            ctx.lineWidth = 14;
            ctx.stroke(path);

            // Layer 2: Inner intense glow
            ctx.globalAlpha = 0.5;
            ctx.lineWidth = 8;
            ctx.stroke(path);

            // Layer 1: The solid core thread
            ctx.globalAlpha = 1.0;
            ctx.lineWidth = 2;
            ctx.strokeStyle = '#ffffff';
            ctx.stroke(path);
            
            ctx.globalCompositeOperation = 'screen';
            ctx.strokeStyle = colors[b];
            ctx.stroke(path);

            ctx.globalAlpha = 1.0; // reset for next band
        }

        ctx.restore();
    }

    // RENDER IMPACT
    if (d.phase === 'IMPACT') {
        const blastRadius = tImpact * 180; // slightly larger blast
        const blastAlpha = 1 - tImpact;

        ctx.save();
        ctx.globalAlpha = blastAlpha;
        ctx.translate(eff.tx, eff.ty);

        // Massive Holy Light Wave (White and Gold)
        const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, blastRadius);
        grad.addColorStop(0, 'rgba(255, 255, 255, 1)');
        grad.addColorStop(0.2, 'rgba(255, 215, 0, 0.9)'); // Gold
        grad.addColorStop(0.5, 'rgba(255, 235, 150, 0.4)'); // Light Gold
        grad.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(0, 0, blastRadius, 0, Math.PI * 2);
        ctx.fill();

        // Inner high-intensity flash
        const innerRadius = blastRadius * 0.4;
        const innerGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, innerRadius);
        innerGrad.addColorStop(0, 'rgba(255, 255, 255, 1)');
        innerGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.fillStyle = innerGrad;
        ctx.beginPath();
        ctx.arc(0, 0, innerRadius, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();

        // Shards and lingering motes, draw with 'screen' composite
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        d.shards.forEach(s => s.draw(ctx));
        ctx.restore();
    }

    d.motes.forEach(m => m.draw(ctx));
    ctx.restore();
    return true;
}
